import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Loader2, ShieldCheck, User } from "lucide-react";

export default function KelolaUser() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState({ username: "", nama: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const tambahUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`User "${form.username}" berhasil dibuat`);
      setShowAdd(false);
      setForm({ username: "", nama: "", password: "", role: "user" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal membuat user"));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${resetTarget.id}`, { password: newPassword });
      toast.success(`Password "${resetTarget.username}" berhasil direset`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(apiError(err, "Gagal mereset password"));
    } finally {
      setSaving(false);
    }
  };

  const hapusUser = async (u) => {
    if (!window.confirm(`Hapus user "${u.username}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User berhasil dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus user"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Kelola User</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tambah user entry, reset password, atau hapus akun.
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          data-testid="admin-add-user-button"
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          <UserPlus className="mr-2 h-4 w-4" /> Tambah User
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-user-management-table">
              <thead>
                <tr className="bg-slate-900 text-left text-white">
                  <th className="px-4 py-3 font-semibold">Username</th>
                  <th className="px-4 py-3 font-semibold">Nama</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-slate-500">Memuat data...</td>
                  </tr>
                )}
                {!loading &&
                  users.map((u, i) => (
                    <tr key={u.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`user-row-${u.username}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{u.username}</td>
                      <td className="px-4 py-3 text-slate-600">{u.nama}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            u.role === "admin"
                              ? "bg-slate-900 text-white"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                          data-testid={`user-role-${u.username}`}
                        >
                          {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => { setResetTarget(u); setNewPassword(""); }}
                            data-testid={`user-reset-button-${u.username}`}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-900 hover:text-white"
                            title="Reset Password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                          {u.id !== me?.id && (
                            <button
                              onClick={() => hapusUser(u)}
                              data-testid={`user-delete-button-${u.username}`}
                              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-rose-600 hover:text-white"
                              title="Hapus"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Tambah User Baru</DialogTitle>
            <DialogDescription>User baru dapat langsung login untuk entry data saldo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={tambahUser} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-username">Username</Label>
              <Input
                id="new-username"
                data-testid="add-user-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-nama">Nama Lengkap</Label>
              <Input
                id="new-nama"
                data-testid="add-user-nama-input"
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Password (min. 6 karakter)</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="add-user-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user" data-testid="role-option-user">User (entry data)</SelectItem>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)} data-testid="add-user-cancel-button">
                Batal
              </Button>
              <Button type="submit" disabled={saving} data-testid="add-user-submit-button" className="bg-emerald-600 text-white hover:bg-emerald-700">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Simpan User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetTarget?.username}</DialogTitle>
            <DialogDescription>Masukkan password baru untuk user ini.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="reset-password">Password Baru (min. 6 karakter)</Label>
            <Input
              id="reset-password"
              type="password"
              data-testid="reset-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="reset-cancel-button">
              Batal
            </Button>
            <Button onClick={resetPassword} disabled={saving} data-testid="reset-submit-button" className="bg-emerald-600 text-white hover:bg-emerald-700">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
